import { mapGetters } from "vuex";
import { defineComponent } from "vue";
import { SdkCompany } from "@/interfaces/sdk-company";
import { getCookie } from "@/utils/cookie";
import { loadCategories, deleteCategory, findProcesses, loadPaginatedCategories } from "@/store/flow/actions";
import { findByUserAndMenuAndEnvironment } from "@/store/landing-page/actions";
import { findVersionInfoFlow } from "@/store/version/actions";
import { SdkExceptLandingPage } from "@/interfaces/sdk-dictionary";
import { saveCheckLandingPageFlow } from "@/store/flow/actions";
import { INotification, ETypeNotification } from "@/interfaces/i-notification";
import { EventBus } from "@/utils/event-bus";
import SdkFormsSearch from "@/components/sdk-forms/sdk-forms-search/sdk-forms-search.vue";
import { sort } from "@/utils/array-functions";
import { updateCategory } from "@/store/flow/actions";
import SdkFlowSearchResult from "@/components/sdk-flow-search-result/sdk-flow-search-result.vue";
import { tokenInfo } from "@/store/login/types";

export default defineComponent({
  name: "sdk-flow-categories",
  components: {
    SdkFormsSearch,
    SdkFlowSearchResult,
  },
  data() {
    return {
      loading: false,
      notification: [] as INotification[],
      categories: [] as SdkCompany[],
      list: [] as SdkCompany[],
      versionInfo: "",
      landingPage: false,
      showPage: false,
      showDeleteDialog: false,
      showEditDialog: false,
      categoryName: "",
      categoryId: "",
      categoryDescription: "",
      exceptLandingPage: {
        id: undefined,
        environment: { id: undefined },
        menu: { id: undefined },
        user: "",
        version: "",
      } as SdkExceptLandingPage,
      processSearchResult: [] as any[],
      searchModifier: "category",
      dialogLoading: false,
      paginationConfig: {
        currentPage: 1,
        totalPages: 0,
      },
      userQuery: "",
    };
  },
  computed: {
    ...mapGetters({
      tokenInfo,
    }),
    searchOptions(): { label: string; value: "category" | "process" }[] {
      return [
        { label: this.$tc("flow.categories.filterOptions.categories"), value: "category" },
        { label: this.$tc("flow.categories.filterOptions.processes"), value: "process" },
      ];
    },
    filteredList(): SdkCompany[] {
      return sort(this.list, false, "name");
    },
    searchPlaceholder(): string {
      return this.searchModifier === "category" ? this.$tc("flow.categories.categoriesSearch") : this.$tc("flow.category.search");
    },
  },
  methods: {
    setSearchModifier(modifier: string) {
      this.searchModifier = modifier;
    },
    startCreation() {
      this.$router.push({ name: "flow-category-new" });
    },
    async findVersionInfo() {
      await this.$dispatch(findVersionInfoFlow)().then((res: any) => {
        this.versionInfo = res.app.name + "-" + res.app.version;
      });
    },
    async reloadCategories() {
      await this.$dispatch(loadCategories)().then((data: any) => {
        this.categories = data;
        this.list = data;
      });
    },
    openDeleteDialog(categoryId: string, categoryName: string) {
      this.showDeleteDialog = true;
      this.categoryName = categoryName;
      this.categoryId = categoryId;
    },
    openEditDialog(categoryId: string, categoryName: string, categoryDescription: string) {
      this.showEditDialog = true;
      this.categoryId = categoryId;
      this.categoryName = categoryName;
      this.categoryDescription = categoryDescription;
    },
    closeDeleteDialog() {
      this.showDeleteDialog = false;
    },
    closeEditDialog() {
      this.showEditDialog = false;
    },
    async removeCategory() {
      await this.$dispatch(deleteCategory)([this.categoryId]).then(() => {
        this.reloadCategories();
        this.closeDeleteDialog();
        this.notification.push({
          type: ETypeNotification.SUCCESS,
          title: this.$t("flow.categories.modal.deleteCategory.notifications.delete.title") as string,
          message: this.$t("flow.categories.modal.deleteCategory.notifications.delete.message") as string,
        });
        EventBus.$emit("emmit-notification", this.notification[0]);
        this.notification = [];
      });
    },
    async updateCategory() {
      this.dialogLoading = true;
      const category = {
        id: this.categoryId,
        name: this.categoryName,
        environmentId: this.tokenInfo.environment.id,
        description: this.categoryDescription,
      };
      await this.$dispatch(updateCategory)([category], this.$t("flow.categories.modal.editCategory.notifications.edit.message"))
        .then(() => {
          this.reloadCategories();
          this.closeEditDialog();
        })
        .finally(() => {
          this.dialogLoading = false;
        });
    },
    async filterResult(value: string, modifier: string) {
      if (modifier === "category") {
        this.userQuery = value;
        this.paginationConfig.currentPage = 1;
        this.getCategories();
        this.processSearchResult = [];
      } else {
        this.searchModifier = "process";
        if (value) {
          this.loading = true;
          await this.$dispatch(findProcesses)([value]).then(async (res: any[]) => {
            this.processSearchResult = res;
          });
          this.loading = false;
        } else {
          this.processSearchResult = [];
        }
      }
    },
    getCategories() {
      this.loading = true;
      this.$dispatch(loadPaginatedCategories)([this.paginationConfig.currentPage, this.userQuery])
        .then((res: any) => {
          this.paginationConfig.totalPages = res.pagination.totalPages;
          this.categories = res.data;
          this.list = res.data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  beforeRouteEnter(to: any, from: any, next: any) {
    const { name } = from;
    if (name !== "landing-flow") {
      next(async (vm: any) => {
        await vm
          .$dispatch(findVersionInfoFlow)()
          .then((res: any) => {
            vm.versionInfo = res.app.name + "-" + res.app.version;
          });
        await vm
          .$dispatch(findByUserAndMenuAndEnvironment)([vm.tokenInfo.user_name, vm.$route.params.menuId, vm.tokenInfo.environment.id])
          .then((res: any) => {
            vm.landingPage = res;
          });
        if (vm.landingPage) {
          vm.$router.push({ name: "landing-flow" });
        } else {
          next((vm.showPage = true));
        }
      });
    } else {
      next((vm: any) => (vm.showPage = true));
    }
  },
  async mounted() {
    this.loading = true;
    if (getCookie("checkLandingFlow")) {
      await this.findVersionInfo();
      this.exceptLandingPage = {
        id: undefined,
        environment: { id: this.tokenInfo.environment.id },
        menu: { id: Number(this.$route.params.menuId) },
        user: this.tokenInfo.user_name,
        version: this.versionInfo,
      };
      await this.$dispatch(saveCheckLandingPageFlow)([this.exceptLandingPage]);
      document.cookie = "checkLandingFlow" + "=; Max-Age=0";
    }
    this.loading = false;
    this.getCategories();
  },
});
